import { Injectable } from "@angular/core";
import { AppConfig } from "src/app/app.config";
import { LoadRemoteModuleOptions } from "@angular-architects/module-federation-runtime";
import { loadRemoteModuleWithRetry } from "./load-remote-module-with-retry";

export type MicrofrontendLoadOptions = LoadRemoteModuleOptions & {
  elementName: string;
};

/**
 * Loads microfrontend remotes with retries and per-tenant remote entries, for the actions
 * that mount microfrontend elements on demand. Mirrors the loading in CustomWebComponentWrapperComponent:
 * keep both in sync when the remote loading or tenant resolution changes.
 */
@Injectable({
  providedIn: "root",
})
export class MicrofrontendLoaderService {
  // Custom element name -> microfrontend name used in the tenant subdomain
  private readonly microfrontendNames: Record<string, string> = {
    "mf-hd-root": "dashboard",
    "mf-hr-root": "connect",
    "mf-hl-root": "leaves",
    "mf-hl-assign-leave": "leaves",
  };

  async load(options: MicrofrontendLoadOptions): Promise<void> {
    const resolvedOptions = this.resolveRemoteEntry(options);

    if (
      "remoteEntry" in resolvedOptions &&
      !/^https?:\/\//.test(resolvedOptions.remoteEntry)
    ) {
      throw new Error(
        `Invalid remoteEntry '${resolvedOptions.remoteEntry}' for '${resolvedOptions.elementName}'`,
      );
    }

    try {
      await this.loadOptionsWithRetry(resolvedOptions);
    } catch (customError) {
      if (
        resolvedOptions === options ||
        !("remoteEntry" in resolvedOptions) ||
        !("remoteEntry" in options) ||
        resolvedOptions.remoteEntry === options.remoteEntry
      ) {
        throw customError;
      }

      await this.loadOptionsWithRetry(options);
    }
  }

  private async loadOptionsWithRetry(
    options: MicrofrontendLoadOptions,
  ): Promise<void> {
    await loadRemoteModuleWithRetry(options);
  }

  private resolveRemoteEntry(
    options: MicrofrontendLoadOptions,
  ): MicrofrontendLoadOptions {
    const microfrontendName = this.microfrontendNames[options.elementName];

    if (!microfrontendName || !("remoteEntry" in options)) {
      return options;
    }

    const hostname = window.location.hostname;
    const subdomain = hostname.includes(".") ? hostname.split(".")[0] : "";

    if (!subdomain || this.isDefaultSubdomain(subdomain)) {
      return options;
    }

    return {
      ...options,
      remoteEntry: `${window.location.protocol}//${subdomain}-${microfrontendName}.${hostname.split(".").slice(1).join(".")}/remoteEntry.js`,
    };
  }

  private isDefaultSubdomain(subdomain: string): boolean {
    return [
      "app",
      "cppa",
      "cppatest",
      AppConfig.settings.application.subdomain,
    ].includes(subdomain);
  }
}
